/**
 * Textarea — multi-line free-form text (theme T5a §3).
 *
 * Auto-grows up to 400 px via `field-sizing: content` where supported
 * (Chrome 123+ / Safari 17+).  Falls back to fixed min-height elsewhere.
 * Explicit `rows` prop overrides the default.
 */
import type { TextareaHTMLAttributes } from 'react';
import { forwardRef } from 'react';

import { cn } from './cn';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  success?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, disabled, readOnly, success, rows = 3, ...rest },
  ref,
) {
  const invalid = rest['aria-invalid'] === true || rest['aria-invalid'] === 'true';

  return (
    <textarea
      ref={ref}
      className={cn(
        'max-h-[400px] min-h-24 w-full resize-y rounded-md border bg-white px-3 py-3 text-base text-slate-800 transition-colors duration-fast ease-standard placeholder:text-fg-muted',
        'border-slate-300 hover:border-slate-400',
        'focus:ring-brand-primary-400/50 focus:border-brand-primary-500 focus:outline-none focus:ring-2',
        invalid && 'focus:ring-error-600/50 border-error-600 focus:border-error-600',
        success && !invalid && 'border-success-600 focus:border-success-600',
        disabled && 'cursor-not-allowed bg-slate-50 opacity-disabled',
        readOnly && 'bg-slate-50',
        // field-sizing is progressive enhancement — feature-detected.
        '[field-sizing:content]',
        className,
      )}
      disabled={disabled}
      readOnly={readOnly}
      rows={rows}
      {...rest}
    />
  );
});
